/** Preferencias MIME para MediaRecorder (Chrome suele usar webm/opus). */
export function pickRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined') return '';
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ];
  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported(type)) return type;
  }
  return '';
}

export function extensionForMime(mime) {
  if (!mime) return 'webm';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4') || mime.includes('m4a')) return 'm4a';
  if (mime.includes('wav')) return 'wav';
  return 'webm';
}

/**
 * Graba audio hasta silencio (después de hablar) o stop() manual.
 *
 * @param {{
 *   mode?: 'silence' | 'hold',
 *   silenceMs?: number,
 *   minSpeechMs?: number,
 *   maxMs?: number,
 *   silenceThreshold?: number,
 *   onSpeechStart?: () => void,
 *   onSilenceProgress?: (ms: number) => void,
 * }} [opts]
 */
export async function startVoiceRecording(opts = {}) {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('Este navegador no permite grabar audio.');
  }
  if (typeof MediaRecorder === 'undefined') {
    throw new Error('MediaRecorder no está disponible en este navegador.');
  }

  const mode = opts.mode === 'hold' ? 'hold' : 'silence';
  const silenceMs = opts.silenceMs ?? 1100;
  const minSpeechMs = opts.minSpeechMs ?? 350;
  const maxMs = opts.maxMs ?? (mode === 'hold' ? 30000 : 15000);
  const silenceThreshold = opts.silenceThreshold ?? 0.018;

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      channelCount: 1,
    },
  });

  const mimeType = pickRecorderMimeType();
  const recorderOpts = mimeType ? { mimeType, audioBitsPerSecond: 24000 } : { audioBitsPerSecond: 24000 };
  let recorder;
  try {
    recorder = new MediaRecorder(stream, recorderOpts);
  } catch {
    recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
  }

  const chunks = [];
  recorder.ondataavailable = (ev) => {
    if (ev.data && ev.data.size > 0) chunks.push(ev.data);
  };

  let stoppedResolve;
  let finished = false;
  let cleaned = false;
  let settled = false;
  const stopped = new Promise((resolve) => {
    stoppedResolve = resolve;
  });

  const settle = (blob) => {
    if (settled) return;
    settled = true;
    stoppedResolve(blob);
  };

  // Solo modo silencio necesita analyser; en hold evitamos AudioContext (más rápido al soltar).
  const needVad = mode === 'silence';
  let audioCtx = null;
  let source = null;
  let analyser = null;
  let timeData = null;
  if (needVad) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    source = audioCtx.createMediaStreamSource(stream);
    analyser = audioCtx.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.4;
    source.connect(analyser);
    timeData = new Float32Array(analyser.fftSize);
  }

  let rafId = 0;
  let speechStarted = false;
  let speechAccumMs = 0;
  let silenceAccumMs = 0;
  let lastTs = performance.now();
  let maxTimer = null;

  const cleanupAudio = () => {
    if (cleaned) return;
    cleaned = true;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    if (maxTimer) {
      clearTimeout(maxTimer);
      maxTimer = null;
    }
    try {
      source?.disconnect();
    } catch {
      /* noop */
    }
    if (audioCtx && audioCtx.state !== 'closed') {
      Promise.resolve(audioCtx.close()).catch(() => {});
    }
  };

  const stopTracks = () => {
    try {
      stream.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch {
          /* noop */
        }
      });
    } catch {
      /* noop */
    }
  };

  const finish = () => {
    if (finished) return;
    finished = true;
    cleanupAudio();
    try {
      if (recorder.state !== 'inactive') {
        recorder.stop();
      } else {
        stopTracks();
        settle(new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }));
      }
    } catch {
      stopTracks();
      settle(new Blob(chunks, { type: recorder.mimeType || mimeType || 'audio/webm' }));
    }
  };

  recorder.onstop = () => {
    cleanupAudio();
    stopTracks();
    const type = recorder.mimeType || mimeType || 'audio/webm';
    settle(new Blob(chunks, { type }));
  };

  const tick = () => {
    if (finished) return;
    // En modo hold solo el usuario corta; el analyser no auto-finaliza.
    if (mode === 'hold') {
      rafId = requestAnimationFrame(tick);
      return;
    }

    const now = performance.now();
    const dt = Math.min(80, now - lastTs);
    lastTs = now;

    try {
      analyser.getFloatTimeDomainData(timeData);
    } catch {
      finish();
      return;
    }

    let sum = 0;
    for (let i = 0; i < timeData.length; i += 1) {
      const v = timeData[i];
      sum += v * v;
    }
    const rms = Math.sqrt(sum / timeData.length);

    if (rms >= silenceThreshold) {
      if (!speechStarted) {
        speechStarted = true;
        opts.onSpeechStart?.();
      }
      speechAccumMs += dt;
      silenceAccumMs = 0;
    } else if (speechStarted) {
      silenceAccumMs += dt;
      opts.onSilenceProgress?.(silenceAccumMs);
      if (speechAccumMs >= minSpeechMs && silenceAccumMs >= silenceMs) {
        finish();
        return;
      }
    }

    rafId = requestAnimationFrame(tick);
  };

  recorder.start(120);
  if (audioCtx && audioCtx.state === 'suspended') {
    try {
      await audioCtx.resume();
    } catch {
      /* noop */
    }
  }
  lastTs = performance.now();
  if (mode === 'silence') {
    rafId = requestAnimationFrame(tick);
  }
  maxTimer = setTimeout(() => finish(), maxMs);

  return {
    mimeType: recorder.mimeType || mimeType || 'audio/webm',
    stop: () =>
      new Promise((resolve, reject) => {
        try {
          stopped.then(resolve).catch(reject);
          finish();
        } catch (err) {
          stopTracks();
          reject(err);
        }
      }),
    cancel: () => {
      if (finished && settled) return;
      finished = true;
      cleanupAudio();
      try {
        recorder.onstop = null;
        if (recorder.state !== 'inactive') recorder.stop();
      } catch {
        /* noop */
      }
      stopTracks();
      settle(new Blob([], { type: 'audio/webm' }));
    },
  };
}
